import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// Actions
import {
  getPoiHierarchy,
  setPoiStatus,
  setSelected,
} from "../../../actions/map-view/poi/PoiActions";
import {
  selectPoiBrand,
  unSelectPoiBrands,
} from "../../../actions/map-view/poi/PoiSelectionActions";

// Constants and Utils
import { getMergedCityId } from "../CityInfoTempFixUtil";
import { Status } from "../../../constants/GeneralConstants";

// Components
import Spinner from "../../../components/spinner/Spinner";
import NestedList from "../../../components/nested-list/NestedList";
import { Input } from "../../../components/form-input/FormInput";

// get Filtered List On Search
function getFilteredListOnSearch(dataList = [], searchText = "") {
  if (!searchText) {
    return dataList;
  }

  // filtered
  const filtered = dataList.reduce((acc, curr) => {
    const matchedChildren = curr.children.filter((child) =>
      child.label.toLowerCase().includes(searchText.toLowerCase())
    );

    if (matchedChildren.length > 0) {
      acc.push({ ...curr, children: matchedChildren });
    }

    return acc;
  }, []);

  return filtered;
}

function SearchPoiSection({
  dataList = [],
  setPoisList = () => {},
  searchedText = "",
  setSearchedText = () => {},
}) {
  // page filter
  function handleSearch(_id, value) {
    // update state
    setSearchedText(value);

    // filtered list
    const filteredList = getFilteredListOnSearch(dataList, value);

    // update status to open first poiType by default when search happen
    if (filteredList.length > 0) {
      filteredList.forEach((item, index) => {
        item.isSelected = index === 0;
      });
    }

    // update list
    setPoisList([...filteredList]);
  }

  return (
    <Input
      type="search"
      placeHolder="Search points of interest"
      value={searchedText}
      onChange={handleSearch}
    />
  );
}

/**
 * Page Section
 */
function PoiListingSection({ dataList, cityId }) {
  const dispatch = useDispatch();

  // State
  const [poisList, setPoisList] = useState([]);
  const [searchedText, setSearchedText] = useState("");

  // Selector State
  const getPoiLoading = useSelector((state) => state.poi.getPoiLoading);
  const dataListCityId = useSelector((state) => state.poi.cityId);

  const brandLabelToId = useSelector((state) => state.poi.brandLabelToId);
  const sectorLabelToId = useSelector((state) => state.poi.sectorLabelToId);
  const sectorToBrandMap = useSelector((state) => state.poi.sectorToBrandMap);
  const brandIdToLabel = useSelector((state) => state.poi.brandIdToLabel);
  const brandLoadingMap = useSelector((state) =>
    Object.keys(state.poiSelection.getBrandInfoLoading).reduce(
      (acc, eachBrandId) => {
        if (!state.poiSelection.getBrandInfoLoading[eachBrandId]) {
          return acc;
        }
        const brandLabel = brandIdToLabel[eachBrandId];
        acc[brandLabel] = true;
        return acc;
      },
      {}
    )
  );

  // to handle the "city-delhi-ncr" and "city-greater-mumbai" case
  const updatedCityId = getMergedCityId(cityId);

  // Dispatch
  useEffect(() => {
    if (dataList.length === 0 || dataListCityId !== updatedCityId) {
      dispatch(getPoiHierarchy(updatedCityId));
    }
  }, [dispatch]);

  // update list when any change happen on selection with searched list
  useEffect(() => {
    const newDataList = getFilteredListOnSearch(dataList, searchedText);
    setPoisList(newDataList);
  }, [JSON.stringify(dataList)]);

  // Functions
  function actionOnAddButtonSelect(selectedIndex, label) {
    dispatch(setSelected(selectedIndex, label));
  }

  function actionOnAddButtonStatus(selectedIndex, label, status) {
    const brandId = brandLabelToId[label];
    if (brandId) {
      status === Status.CHECKED
        ? dispatch(selectPoiBrand(updatedCityId, brandId))
        : dispatch(unSelectPoiBrands([brandId]));
      return;
    }
    const sectorId = sectorLabelToId[label];
    const brandIds = sectorToBrandMap[sectorId];
    status === Status.CHECKED
      ? brandIds.forEach((brandId) =>
          dispatch(selectPoiBrand(updatedCityId, brandId))
        )
      : dispatch(unSelectPoiBrands(brandIds));
  }

  function actionOnAddButton(label, status) {
    dispatch(setPoiStatus(label, status));
  }

  return (
    <>
      {/* Search Input */}
      <SearchPoiSection
        dataList={dataList}
        setPoisList={setPoisList}
        searchedText={searchedText}
        setSearchedText={setSearchedText}
      />
      <hr className="my-2" />

      {/* Loading */}
      {getPoiLoading && (
        <div className="d-flex justify-content-center">
          <Spinner />
        </div>
      )}

      {/* POI NestedList */}
      {!getPoiLoading && (
        <NestedList
          data={poisList}
          brandLoadingMap={brandLoadingMap}
          setSelected={actionOnAddButtonSelect}
          setAdded={actionOnAddButton}
          setAddButtonStatus={actionOnAddButtonStatus}
          selectAllAttr={true}
        />
      )}
    </>
  );
}

export default PoiListingSection;
